import { CartAddEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cadeau offert dans le cart drawer (Maillot EDF 2026 - Domicile / Extérieur
 * + taille en propriété de ligne). S'appuie sur les mêmes routes Ajax et les
 * mêmes events (CartAddEvent / CartUpdateEvent) que le reste du thème
 * Horizon, pour que le drawer se remorph automatiquement après ajout ou
 * retrait.
 *
 * @extends {HTMLElement}
 */
class CartFreeGiftComponent extends HTMLElement {
  /** @type {string} Côté choisi ("Domicile" / "Extérieur" / "") */
  #selectedSide = '';

  /** @type {string} Taille choisie ("S", "M", "L", "XL", "2XL" / "") */
  #selectedSize = '';

  connectedCallback() {
    this.#selectedSide = this.dataset.currentSide || '';
    this.#selectedSize = this.dataset.currentSize || '';
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const toggleButton = target.closest('[data-action="toggle"]');
    const sideButton = target.closest('.cart-free-gift__option');
    const sizeButton = target.closest('.cart-free-gift__size');
    const removeButton = target.closest('[data-action="remove"]');

    if (toggleButton) {
      this.#toggleExpanded();
    } else if (sideButton instanceof HTMLButtonElement && !sideButton.disabled) {
      this.#onSideClick(sideButton);
    } else if (sizeButton instanceof HTMLButtonElement) {
      this.#onSizeClick(sizeButton);
    } else if (removeButton) {
      this.#removeGift();
    }
  };

  #toggleExpanded() {
    const isExpanded = this.getAttribute('data-expanded') === 'true';
    this.setAttribute('data-expanded', isExpanded ? 'false' : 'true');
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #onSideClick(button) {
    const side = button.dataset.side;
    if (!side || side === this.#selectedSide) return;

    this.#selectedSide = side;
    this.#toggleSelected('.cart-free-gift__option', button);
    this.#syncIfReady();
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #onSizeClick(button) {
    const size = button.dataset.size;
    if (!size || size === this.#selectedSize) return;

    this.#selectedSize = size;
    this.#toggleSelected('.cart-free-gift__size', button);
    this.#syncIfReady();
  }

  /**
   * @param {string} selector
   * @param {HTMLButtonElement} activeButton
   */
  #toggleSelected(selector, activeButton) {
    this.querySelectorAll(selector).forEach((el) => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-pressed', 'false');
    });
    activeButton.classList.add('is-selected');
    activeButton.setAttribute('aria-pressed', 'true');
  }

  /**
   * Dès que le côté ET la taille sont choisis, on (ré)ajoute le cadeau.
   * Si un cadeau était déjà dans le panier, on le retire d'abord.
   */
  async #syncIfReady() {
    if (!this.#selectedSide || !this.#selectedSize) return;

    const newVariantId = this.#selectedSide === 'Domicile' ? this.dataset.variantDom : this.dataset.variantExt;
    if (!newVariantId) return;

    const currentVariantId = this.dataset.currentVariant;
    const currentSide = this.dataset.currentSide;
    const currentSize = this.dataset.currentSize;

    // Déjà exactement cette combinaison dans le panier : rien à faire.
    if (
      currentVariantId === newVariantId &&
      currentSide === this.#selectedSide &&
      currentSize === this.#selectedSize
    ) {
      return;
    }

    this.#setLoading(true);

    try {
      if (currentVariantId) {
        await this.#changeVariantQuantity(currentVariantId, 0);
      }
      await this.#addGiftVariant(newVariantId, this.#selectedSide, this.#selectedSize);

      // On met à jour nous-mêmes l'état "actuel" du composant, plutôt que
      // de dépendre d'un re-rendu serveur qui ne se produit pas forcément
      // de la même façon partout (drawer vs page /cart complète).
      this.dataset.currentVariant = newVariantId;
      this.dataset.currentSide = this.#selectedSide;
      this.dataset.currentSize = this.#selectedSize;

      window.setTimeout(() => {
        this.setAttribute('data-expanded', 'false');
      }, 550);
      this.#setLoading(false);
    } catch (error) {
      console.error('[cart-free-gift]', error);
      this.#setLoading(false);
    }
  }

  async #removeGift() {
    const currentVariantId = this.dataset.currentVariant;
    if (!currentVariantId) return;

    this.#setLoading(true);

    try {
      await this.#changeVariantQuantity(currentVariantId, 0);
      this.dataset.currentVariant = '';
      this.dataset.currentSide = '';
      this.dataset.currentSize = '';
      this.#selectedSide = '';
      this.#selectedSize = '';
      this.querySelectorAll('.cart-free-gift__option, .cart-free-gift__size').forEach((el) => {
        el.classList.remove('is-selected');
        el.setAttribute('aria-pressed', 'false');
      });
      this.#setLoading(false);
    } catch (error) {
      console.error('[cart-free-gift]', error);
      this.#setLoading(false);
    }
  }

  /**
   * IDs des sections cart-items-component présentes sur la page, pour
   * demander leur HTML à jour dans la même requête (comme le fait le thème).
   * @returns {string[]}
   */
  get #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.querySelectorAll('.cart-free-gift__option, .cart-free-gift__size, .cart-free-gift__remove').forEach(
      (el) => {
        if (el instanceof HTMLButtonElement) el.disabled = isLoading;
      }
    );
  }

  /**
   * Retire (ou ajuste) la variante cadeau actuellement dans le panier.
   * @param {string} variantId
   * @param {number} quantity
   */
  async #changeVariantQuantity(variantId, quantity) {
    const body = JSON.stringify({
      id: variantId,
      quantity,
      sections: this.#sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : 'cart_change_error');
    }

    this.dispatchEvent(
      new CartUpdateEvent(data, 'cart-free-gift', {
        source: 'cart-free-gift',
        itemCount: data.item_count,
        sections: data.sections,
      })
    );

    return data;
  }

  /**
   * Ajoute la variante cadeau (0,00€) avec des propriétés de ligne pour
   * retrouver ensuite le choix (côté + taille) et pouvoir le retirer.
   * @param {string} variantId
   * @param {string} side
   * @param {string} size
   */
  async #addGiftVariant(variantId, side, size) {
    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('properties[_cadeau_edf_2026]', side);
    formData.set('properties[Cadeau]', `Maillot EDF 2026 - ${side}`);
    formData.set('properties[Taille]', size);
    this.#sectionIds.forEach((id) => formData.append('sections', id));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    });
    const data = await response.json();

    if (data.status) {
      this.dispatchEvent(new CartErrorEvent('cart-free-gift', data.message, data.description, data.errors));
      throw new Error(data.message || 'cart_add_error');
    }

    this.dispatchEvent(
      new CartAddEvent(data, 'cart-free-gift', {
        source: 'cart-free-gift',
        itemCount: data.item_count,
        variantId,
        sections: data.sections,
      })
    );

    return data;
  }
}

if (!customElements.get('cart-free-gift-component')) {
  customElements.define('cart-free-gift-component', CartFreeGiftComponent);
}

/**
 * Petit composant "invisible" : dès qu'il apparaît dans le panier (c'est-à-dire
 * dès que le serveur détecte que le cadeau est encore présent alors que le
 * panier n'atteint plus le seuil des 60€, par ex. après la suppression d'un
 * article), il retire automatiquement le maillot offert du panier.
 *
 * @extends {HTMLElement}
 */
class CartGiftAutoRemove extends HTMLElement {
  connectedCallback() {
    if (this.dataset.done === 'true') return;
    this.dataset.done = 'true';
    this.#removeGift();
  }

  async #removeGift() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    try {
      const body = JSON.stringify({
        id: variantId,
        quantity: 0,
        sections: this.#sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors) {
        throw new Error(typeof data.errors === 'string' ? data.errors : 'cart_change_error');
      }

      this.dispatchEvent(
        new CartUpdateEvent(data, 'cart-gift-auto-remove', {
          source: 'cart-gift-auto-remove',
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[cart-gift-auto-remove]', error);
    }
  }

  get #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }
}

if (!customElements.get('cart-gift-auto-remove')) {
  customElements.define('cart-gift-auto-remove', CartGiftAutoRemove);
}